import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import { DisputeTransactionType, Transaction } from "model";

/**
* @class TransactionDispute
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.transactionDisputes[0];
* const transactionDispute = new transaction(record);
* @property {STRING} _id - id of the dispute
* @property {DisputeTransactionType} _type - type of dispute
* @property {Transaction} _transaction - disputed transaction
* @property {BOOLEAN} _active - is the dispute active
* @property {STRING} _whyAreYouDisputingThisTransaction - answer to this question
*/

export default class TransactionDispute extends BCMSRecord {
  _id 
  _type
  _transaction
  _confirmationNumber
  _active
  _whyAreYouDisputingThisTransaction
  _didYouMakeThisTransaction
  _reason
  _additionalComments
  _submissionDate
  _whatAreYouTryingToDispute
  _whatDidYouPurchase
  _whyAreYouDissatisfiedWithTheMerchandise
  _wasTheMerchandiseDeliveredElectronically
  _didYouReturnTheMerchandise
  _howDidYouReturnTheMerchandise
  _whyDidntYouReturnTheMerchandise
  _whenWasTheMerchandiseReturned
  _howMuchOfTheTransactionAmountAreYouDisputing
  _partialDisputeAmount
  _whyAreYouNotDisputingTheFullAmount
  _didYouAttemptToContactTheMerchantToResolveTheIssue
  _whenDidYouAttemptToResolveThisissue
  _whatWasTheMerchantsResponseAndMethodOfCommunication
	_isThereAnyAdditionalInformationYouWouldLikeToProvide
  _howDidYouReturnTheMerchandiseOtherExplanation
  _additionalInfoExplanation
  _whyAreYouDissatisfiedWithTheService
  _isThisAServiceRecurringCharge
  _didYouAttemptToCancelTheService
  _whenDidYouCancelTheService

  constructor(record) {
    super(record)
  }
}

TransactionDispute.define({
  "recordType": "transactionDisputes",
  "recordIDProp": "id",
  "className" : "TransactionDispute",
  "recordProperties": [
   
   /** 
    * @name id
    * @instance
    * @property get {STRING} - gets the id of the transactionDispute 
    * @property set {STRING} - sets the id of the transactionDispute 
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("id", "_id"),

    /** 
     * @name type
     * @instance
     * @property get {Merchant} - gets the merchant of the transactionDispute
     * @property set {Merchant} - sets the merchant of the transactionDispute 
     * @memberof transactionDispute 
     */
    BCMSRecordProperty.mapToClass("type", "_type", () => DisputeTransactionType),

    /** 
     * @name transaction
     * @instance
     * @property get {Merchant} - gets the merchant of the transactionDispute 
     * @property set {Merchant} - sets the merchant of the transactionDispute 
     * @memberof transactionDispute 
     */
    BCMSRecordProperty.mapToClass("transaction", "_transaction", () => Transaction),

    /** 
    * @name confirmationNumber
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("confirmationNumber", "_confirmationNumber"),
    
    /**
     * @name active
     * @instance
     * @property get {BOOLEAN} - gets the credit of the transactionDispute 
     * @property set {BOOLEAN} - sets the credit of the transactionDispute 
     * @memberof transactionDispute 
     */
    BCMSRecordProperty.mapToBoolean("active", "_active"),

    /** 
    * @name whyAreYouDisputingThisTransaction
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whyAreYouDisputingThisTransaction", "_whyAreYouDisputingThisTransaction"),

    /** 
    * @name didYouMakeThisTransaction
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("didYouMakeThisTransaction", "_didYouMakeThisTransaction"),

    /** 
    * @name reason
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("reason", "_reason"),

    /** 
    * @name additionalComments
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("additionalComments", "_additionalComments"),

    /** 
    * @name submissionDate
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToDate("submissionDate", "_submissionDate"),

    /** 
    * @name whatAreYouTryingToDispute
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whatAreYouTryingToDispute", "_whatAreYouTryingToDispute"),

    /** 
    * @name whatDidYouPurchase
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whatDidYouPurchase", "_whatDidYouPurchase"),

    /** 
    * @name whyAreYouDissatisfiedWithTheMerchandise
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whyAreYouDissatisfiedWithTheMerchandise", "_whyAreYouDissatisfiedWithTheMerchandise"),

    /** 
    * @name wasTheMerchandiseDeliveredElectronically
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("wasTheMerchandiseDeliveredElectronically", "_wasTheMerchandiseDeliveredElectronically"),
        
    /** 
    * @name didYouReturnTheMerchandise
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("didYouReturnTheMerchandise", "_didYouReturnTheMerchandise"),
    
    /** 
    * @name howDidYouReturnTheMerchandise
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("howDidYouReturnTheMerchandise", "_howDidYouReturnTheMerchandise"),

    /** 
    * @name whyDidntYouReturnTheMerchandise
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whyDidntYouReturnTheMerchandise", "_whyDidntYouReturnTheMerchandise"),

    /** 
    * @name whenWasTheMerchandiseReturned
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whenWasTheMerchandiseReturned", "_whenWasTheMerchandiseReturned"),
    
    /** 
    * @name howMuchOfTheTransactionAmountAreYouDisputing
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("howMuchOfTheTransactionAmountAreYouDisputing", "_howMuchOfTheTransactionAmountAreYouDisputing"),

    /** 
    * @name partialDisputeAmount
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToCurrency("partialDisputeAmount", "_partialDisputeAmount"),

    /** 
    * @name whyAreYouNotDisputingTheFullAmount
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whyAreYouNotDisputingTheFullAmount", "_whyAreYouNotDisputingTheFullAmount"),

    /** 
    * @name didYouAttemptToContactTheMerchantToResolveTheIssue
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("didYouAttemptToContactTheMerchantToResolveTheIssue", "_didYouAttemptToContactTheMerchantToResolveTheIssue"),
    
    /** 
    * @name whenDidYouAttemptToResolveThisissue 
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whenDidYouAttemptToResolveThisissue", "_whenDidYouAttemptToResolveThisissue"),

    /** 
    * @name whatWasTheMerchantsResponseAndMethodOfCommunication
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whatWasTheMerchantsResponseAndMethodOfCommunication", "_whatWasTheMerchantsResponseAndMethodOfCommunication"),
    
    /** 
    * @name isThereAnyAdditionalInformationYouWouldLikeToProvide
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("isThereAnyAdditionalInformationYouWouldLikeToProvide", "_isThereAnyAdditionalInformationYouWouldLikeToProvide"),
    
    /** 
    * @name howDidYouReturnTheMerchandiseOtherExplanation
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("howDidYouReturnTheMerchandiseOtherExplanation", "_howDidYouReturnTheMerchandiseOtherExplanation"),

    /** 
    * @name additionalInfoExplanation
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("additionalInfoExplanation", "_additionalInfoExplanation"),

    /** 
    * @name whyAreYouDissatisfiedWithTheService
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whyAreYouDissatisfiedWithTheService", "_whyAreYouDissatisfiedWithTheService"),

    /** 
    * @name isThisAServiceRecurringCharge
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("isThisAServiceRecurringCharge", "_isThisAServiceRecurringCharge"),

    /** 
    * @name didYouAttemptToCancelTheService
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("didYouAttemptToCancelTheService", "_didYouAttemptToCancelTheService"),

    /** 
    * @name whenDidYouCancelTheService
    * @instance
    * @property get {STRING} - gets the answer to this question
    * @property set {STRING} - sets the answer to this question
    * @memberof transactionDispute 
    */
    BCMSRecordProperty.mapToString("whenDidYouCancelTheService", "_whenDidYouCancelTheService"),
  ]
})
